import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe, Search, RefreshCw, CheckCircle, AlertCircle, FileText, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_LABELS = {
  queued: '⏳ En cola...',
  running: '🔄 Scrappeando páginas...',
  summarizing: '🤖 IA analizando contenido...',
  done: '✅ Listo',
  error: '❌ Error',
};

export default function WebScraperTool() {
  const [url, setUrl] = useState('');
  const [query, setQuery] = useState('');
  const [maxPages, setMaxPages] = useState(30);
  const [depth, setDepth] = useState(2);
  const [jobId, setJobId] = useState(null);
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => () => stopPolling(), []);

  const startScrape = async () => {
    if (!url.trim()) { toast.error('Ingresa una URL'); return; }
    setLoading(true);
    setJobData(null);
    setJobId(null);
    stopPolling();
    try {
      const res = await axios.post(`${API}/knowledge/scrape`, {
        url: url.trim(), query: query.trim(), max_pages: maxPages, depth,
      });
      const id = res.data.job_id;
      setJobId(id);
      setJobData({ status: 'queued', progress: { pages: 0, current_url: '' } });

      pollRef.current = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/knowledge/scrape/${id}`);
          setJobData(r.data);
          if (r.data.status === 'done' || r.data.status === 'error') {
            stopPolling();
            setLoading(false);
          }
        } catch { stopPolling(); setLoading(false); }
      }, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al iniciar scraping');
      setLoading(false);
    }
  };

  const reset = () => {
    stopPolling();
    setJobId(null);
    setJobData(null);
    setLoading(false);
  };

  const isActive = jobData && ['queued', 'running', 'summarizing'].includes(jobData.status);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="glass-panel rounded-2xl p-5">
        <h3 className="text-white font-bold text-base flex items-center gap-2 mb-1">
          <Globe size={16} className="text-blue-400" /> Deep Web Scraper + IA
        </h3>
        <p className="text-gray-500 text-xs mb-4">
          Extrae y analiza el contenido de cualquier sitio web con IA. Soporta profundidad 2 y hasta 30 páginas.
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-gray-400 text-xs mb-1">URL del sitio *</label>
            <input
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://ejemplo.com"
              className="input-glass w-full text-sm"
              disabled={isActive}
            />
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-1">Consulta / pregunta (opcional)</label>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="¿Qué quieres saber sobre este sitio?"
              className="input-glass w-full text-sm"
              disabled={isActive}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-gray-400 text-xs mb-1">Máx. páginas (1-30)</label>
              <input
                type="number" min={1} max={30}
                value={maxPages}
                onChange={e => setMaxPages(Math.min(30, Math.max(1, parseInt(e.target.value) || 10)))}
                className="input-glass w-full text-sm"
                disabled={isActive}
              />
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1">Profundidad (1-3)</label>
              <select
                value={depth}
                onChange={e => setDepth(parseInt(e.target.value))}
                className="input-glass w-full text-sm"
                disabled={isActive}>
                <option value={1}>1 — Solo página principal</option>
                <option value={2}>2 — Sigue enlaces internos</option>
                <option value={3}>3 — Profundidad máxima</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={isActive ? undefined : startScrape}
            disabled={loading && !isActive}
            className="flex-1 py-2.5 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2 transition-all"
            style={{ background: isActive ? '#6b7280' : '#2563eb', cursor: isActive ? 'not-allowed' : 'pointer' }}>
            {isActive
              ? <><RefreshCw size={14} className="animate-spin" />Procesando...</>
              : <><Search size={14} />Iniciar Scraping</>}
          </button>
          {(jobData || loading) && (
            <button onClick={reset}
              className="px-4 py-2.5 rounded-xl text-sm text-gray-400 border border-gray-600 hover:border-gray-400 transition-all">
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Progress */}
      {jobData && (
        <div className="glass-panel rounded-2xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-white font-semibold text-sm">
              {STATUS_LABELS[jobData.status] || jobData.status}
            </span>
            {jobData.scrape_result && (
              <span className="text-gray-400 text-xs">
                {jobData.scrape_result.pages_scraped} páginas · {Math.round((jobData.scrape_result.total_chars || 0) / 1000)}k caracteres
              </span>
            )}
          </div>

          {isActive && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-700 rounded-full h-1.5">
                  <div
                    className="bg-blue-500 h-1.5 rounded-full transition-all"
                    style={{ width: `${Math.min(100, ((jobData.progress?.pages || 0) / maxPages) * 100)}%` }} />
                </div>
                <span className="text-gray-400 text-xs w-16 text-right">
                  {jobData.progress?.pages || 0} / {maxPages}
                </span>
              </div>
              {jobData.status === 'running' && jobData.progress?.current_url && (
                <p className="text-gray-500 text-xs truncate">
                  Procesando {jobData.progress.pages || 0} página{(jobData.progress.pages || 0) !== 1 ? 's' : ''}...
                  <br /><span className="text-gray-600">{jobData.progress.current_url}</span>
                </p>
              )}
              {jobData.status === 'summarizing' && (
                <p className="text-blue-400 text-xs animate-pulse">
                  🤖 OkBot IA está analizando las {jobData.scrape_result?.pages_scraped} páginas extraídas...
                </p>
              )}
            </div>
          )}

          {/* Pages preview */}
          {jobData.scrape_result?.pages_preview?.length > 0 && (
            <div className="space-y-1">
              <p className="text-gray-500 text-xs font-medium">Páginas procesadas:</p>
              <div className="max-h-28 overflow-y-auto space-y-1">
                {jobData.scrape_result.pages_preview.map((p, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle size={11} className="text-green-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-gray-400 text-xs truncate">{p.title || p.url}</p>
                      <a href={p.url} target="_blank" rel="noreferrer"
                        className="text-gray-600 text-[10px] hover:text-gray-400 flex items-center gap-1 truncate">
                        <ExternalLink size={9} />{p.url}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Errors */}
          {jobData.scrape_result?.errors?.length > 0 && (
            <details className="text-xs">
              <summary className="text-amber-400 cursor-pointer flex items-center gap-1">
                <AlertCircle size={12} />{jobData.scrape_result.errors.length} error(es) menores
              </summary>
              <ul className="mt-1 space-y-0.5 text-gray-600">
                {jobData.scrape_result.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* AI Summary */}
      {jobData?.summary && (
        <div className="glass-panel rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <FileText size={15} className="text-purple-400" />
            <h4 className="text-white font-semibold text-sm">Resumen IA</h4>
          </div>
          <div className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
            {jobData.summary}
          </div>
        </div>
      )}

      {jobData?.error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
          <p className="text-red-400 text-sm"><AlertCircle size={14} className="inline mr-1" />Error: {jobData.error}</p>
        </div>
      )}
    </div>
  );
}
